import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import LinearProgress from '@mui/material/LinearProgress';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import Label from 'src/components/label';

import {
  LoanBorrowerType,
  LoanAgreementStatus,
  ILoanAgreementListItem,
} from 'src/types/loan-agreement';

type Props = {
  row: ILoanAgreementListItem;
};

export function loanListIdLine(row: ILoanAgreementListItem): string {
  if (row.borrowerType === LoanBorrowerType.EMPLOYEE) {
    return `Employee ID: ${row.employee?.employeeNumber ?? '-'}`;
  }
  if (row.nicNumber) return `NIC: ${row.nicNumber}`;
  if (row.phoneNumber) return `Phone: ${row.phoneNumber}`;
  return 'No NIC on record';
}

export default function LoanAgreementTableRow({ row }: Props) {
  const isSettled = row.status === LoanAgreementStatus.SETTLED;

  return (
    <Link
      component={RouterLink}
      href={paths.dashboard.loans.view(row._id)}
      sx={{ textDecoration: 'none', color: 'inherit', display: 'contents' }}
    >
      <TableRow hover>
        <TableCell>
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{row.displayName}</span>
                <Label
                  variant="soft"
                  color={row.borrowerType === LoanBorrowerType.EMPLOYEE ? 'primary' : 'secondary'}
                >
                  {row.borrowerType === LoanBorrowerType.EMPLOYEE ? 'Employee' : 'Other'}
                </Label>
              </Stack>
            }
            secondary={loanListIdLine(row)}
            primaryTypographyProps={{ typography: 'body2', component: 'div' }}
            secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
          />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
          {fCurrencyRupees(row.capitalInCents)}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDate(row.settlementDate)}</TableCell>

        <TableCell sx={{ minWidth: 220 }}>
          <Tooltip
            arrow
            title={
              isSettled
                ? 'Fully repaid'
                : `${fCurrencyRupees(row.remainingInCents)} remaining to pay`
            }
          >
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption">{fCurrencyRupees(row.paidInCents)} paid</Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.progressPercent}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={row.progressPercent}
                color={
                  // eslint-disable-next-line no-nested-ternary
                  isSettled ? 'success' : row.overdueAmountInCents > 0 ? 'warning' : 'primary'
                }
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </Tooltip>
          {row.overdueAmountInCents > 0 && row.status === LoanAgreementStatus.ACTIVE && (
            <Label variant="soft" color="warning" sx={{ mt: 1 }}>
              Overdue · {fCurrencyRupees(row.overdueAmountInCents)}
            </Label>
          )}
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <Button variant="soft" color="primary">
            View
          </Button>
        </TableCell>
      </TableRow>
    </Link>
  );
}
